<template>
  <!-- Thin wrapper over the shared base-input so labels, vee-validate rules and
       server-side error handling all keep working untouched. Only the type
       toggle and the in-field eye button are added. -->
  <base-input
    v-bind="$attrs"
    :value="value"
    :type="visible ? 'text' : 'password'"
    :input-group-classes="groupClasses"
    v-on="$listeners"
  >
    <template #append>
      <!-- Validity indicator, left of the eye. What it means is the caller's
           business: policy compliance on one field, confirmation on the other. -->
      <b-icon-check-circle-fill
        v-if="status === 'valid'"
        class="text-success mr-2"
        :title="validTitle"
        :aria-label="validTitle"
      ></b-icon-check-circle-fill>
      <b-icon-x-circle-fill
        v-else-if="status === 'invalid'"
        class="text-danger mr-2"
        :title="invalidTitle"
        :aria-label="invalidTitle"
      ></b-icon-x-circle-fill>
      <a
        href="#"
        class="text-muted"
        :aria-label="visible ? 'Hide password' : 'Show password'"
        :title="visible ? 'Hide password' : 'Show password'"
        @click.prevent="visible = !visible"
      >
        <b-icon-eye-slash v-if="visible"></b-icon-eye-slash>
        <b-icon-eye v-else></b-icon-eye>
      </a>
    </template>
  </base-input>
</template>

<script>
import { computed, defineComponent, ref } from '@vue/composition-api'

export default defineComponent({
  name: 'BasePasswordInput',
  // Everything except the props below is forwarded straight through to
  // base-input.
  inheritAttrs: false,
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    // 'valid' -> green tick, 'invalid' -> red cross, null -> no indicator.
    // Left null while a field is empty so an untouched form isn't covered in
    // crosses; the caller decides when it starts judging.
    status: {
      type: String,
      default: null,
      validator: (v) => v === null || ['valid', 'invalid'].includes(v)
    },
    // Tooltip/screen-reader text, since the same icon means different things on
    // the password and confirmation fields.
    validTitle: {
      type: String,
      default: 'Valid'
    },
    invalidTitle: {
      type: String,
      default: 'Invalid'
    },
    // Reserves room for the indicator even while it is hidden. Set false on
    // fields that will never show one.
    indicator: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    // Always starts masked; not persisted, so it resets whenever the field
    // is remounted.
    const visible = ref(false)

    // Space is reserved up front rather than when the icon appears - otherwise
    // the text would shift sideways mid-typing.
    const groupClasses = computed(() =>
      props.indicator
        ? 'base-password-input has-match-slot'
        : 'base-password-input'
    )

    return { visible, groupClasses }
  }
})
</script>

<style lang="scss" scoped>
// base-input renders append slots as a bordered .input-group-text cell sitting
// beside the field. Left as-is the eye reads as a separate block rather than
// part of the input, so the cell is lifted out of the flex flow and floated
// over the field instead - that way only the input's own border draws the box.
// Deep selectors are required because this markup belongs to base-input, and
// .input-group is matched as well so these rules outrank Bootstrap's own
// input-group overrides regardless of stylesheet order.
::v-deep .input-group.base-password-input {
  position: relative;

  .form-control {
    // .input-group flattens the right-hand corners of anything that isn't the
    // last child; inherit puts the field's own radius back.
    border-radius: inherit;
    // Room for the eye so a long password never runs underneath it.
    padding-right: 2.75rem;
  }

  // Two icons instead of one, so the field needs the extra room whether or not
  // the tick is currently showing.
  &.has-match-slot .form-control {
    padding-right: 4.25rem;
  }

  .input-group-append {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    // Above the field so the icon stays clickable.
    z-index: 4;
  }

  .input-group-text {
    height: 100%;
    padding: 0 0.75rem;
    background-color: transparent;
    border: 0;
  }
}
</style>
